import { useCallback, useContext, useMemo } from "react"
import { DialogTitle } from "@mui/material"
import { DeepReadonly } from "ts-essentials"
import { toUpper, includes } from "lodash"
import UnifiedSearch from "modules/unified-search"
import { useAccountAlias } from "resources/account"
import { useModelMetadata } from "resources/models"
import { IDashboardContext } from "connected-components/dashboard/context/types"
import { UniversalSearchInstrument } from "helpers/types/instruments/search"
import { getSearchInstrumentIdAndName } from "helpers/search-instruments"
import { SelectedInstrument, SelectedPortfolio } from "helpers/types/instruments/selected"
import { useSaveRecentInstrument } from "hooks/use-save-recent-instrument"
import ModelSelector from "../model-selector"
import type { InstrumentSelectionData } from "../../analytics"

import "./style.scss"

function filterEquitySearchResults(value: UniversalSearchInstrument, modelSupportsComposites: boolean) {
  const isEquity = value.type === "security" && value.assetClass === "equity"
  if (isEquity) {
    // We only support ETFs in models that have composites
    if (value.assetSubclass === "etf") {
      return modelSupportsComposites
    }
    // We never support non-etf equities
    return false
  }

  // We support everything else
  return true
}

export default function DashboardSearch({ context, close, searchType, setInstrument, selectedInstrument, disableModelSelector = false, includeBenchmarks = false, onInstrumentSelection }: DeepReadonly<DashboardSearchProps>) {
  const account = useAccountAlias()
  const { modelId } = useContext(context)
  const [modelMetadata] = useModelMetadata(modelId)

  const modelSupportsComposites = useMemo(
    () => !includes(modelMetadata?.missingDataFiles, "composite"),
    [modelMetadata],
  )

  const { saveRecentInstrument } = useSaveRecentInstrument(modelId)

  const filterSearchResults = useMemo(() =>
    (value: UniversalSearchInstrument) => filterEquitySearchResults(value, modelSupportsComposites),
  [modelSupportsComposites])

  const handleInstrumentSelected = useCallback((searchSelection: UniversalSearchInstrument) => {
    if (!searchSelection) {
      setInstrument(null)
      return
    }

    const isInitialSelection = !selectedInstrument
    const { id, name } = getSearchInstrumentIdAndName(searchSelection)

    onInstrumentSelection?.({ id, name, type: searchSelection.type, searchType, isInitialSelection })

    let newSelectedInstrument: SelectedInstrument | null = null
    let recentInstrument: { instrumentType: string, instrumentId: string }
    switch (searchSelection.type) {
      case "security":
        switch (searchSelection.assetClass) {
          case "swap":
            newSelectedInstrument = { type: "BASKET", id: searchSelection.ticker }
            recentInstrument = { instrumentType: "swap", instrumentId: searchSelection.ticker }
            break
          case "equity":
            // [TODO:TS] Doesn't seem to work if only one option (here, ETFs)
            // switch (searchSelection.assetSubclass) {
            //   case "etf":
            newSelectedInstrument = { type: "ETF", id: searchSelection.internalId }
            recentInstrument = { instrumentType: "asset", instrumentId: `${searchSelection.internalId}.${searchSelection.ticker}.${searchSelection.exchange}` }
            // break
            // default:
            //   // Exhaustiveness check
            //   searchSelection satisfies never
            // }
            break
          default:
            // Exhaustiveness check
            searchSelection satisfies never
            recentInstrument = { instrumentType: "", instrumentId: "" }
        }
        break
      case "experiment": {
        // [TODO:TS] Selected instrument NEEDS to be defined for experiments to be returned. Is there a better way?
        const assertedSelectedInstrument = selectedInstrument as SelectedPortfolio
        newSelectedInstrument = { type: "PORTFOLIO", id: assertedSelectedInstrument.id, experimentId: searchSelection.id }
        recentInstrument = { instrumentType: "experiment", instrumentId: searchSelection.id }
        break
      }
      case "portfolio":
        newSelectedInstrument = { type: "PORTFOLIO", id: searchSelection.id }
        recentInstrument = { instrumentType: "portfolio", instrumentId: searchSelection.id }
        break
      default:
        newSelectedInstrument = { type: toUpper(searchSelection.type), id: searchSelection.id }
        recentInstrument = { instrumentType: searchSelection.type, instrumentId: searchSelection.id }
    }

    setInstrument(newSelectedInstrument)
    saveRecentInstrument(recentInstrument, searchType)
  }, [setInstrument, selectedInstrument, onInstrumentSelection, saveRecentInstrument, searchType])

  const { resourceId, resourceType } = useMemo(() => {
    if (selectedInstrument) {
      return { resourceId: selectedInstrument.id, resourceType: selectedInstrument.type }
    }

    return {}
  }, [selectedInstrument])

  const itemsActions = useMemo(() => {
    const resourceAction = {
      noDispatch: true,
      mainAction: (selectedItem: UniversalSearchInstrument) => () => {
        handleInstrumentSelected(selectedItem)
        close()
      },
    }
    return {
      security: resourceAction,
      portfolio: resourceAction,
      index: resourceAction,
      swap: resourceAction,
      experiment: resourceAction,
      benchmark: resourceAction,
    }
  }, [handleInstrumentSelected, close])

  const hiddenTypes = useMemo(() =>
    includeBenchmarks
      ? ["researchTopics", "watchlists", "factors", "currencies"]
      : ["researchTopics", "watchlists", "factors", "currencies", "benchmarks"],
  [includeBenchmarks])

  const emptySearch = <div className="dashboard-empty-search">
    Search Portfolios, Baskets, {modelSupportsComposites ? " Indexes, or ETFs" : " or Indexes"}
  </div>

  return <div className="components_dashboard-search">
    <DialogTitle className="dashboard-search-title">
      <span>Select Instrument</span>
      <ModelSelector context={context} disabled={disableModelSelector} />
    </DialogTitle>
    <UnifiedSearch
      disablePortal
      autoFocus
      hiddenTypes={hiddenTypes}
      instrument={selectedInstrument}
      searchType={searchType}
      account={account}
      modelId={modelId}
      compositeAssetType={resourceType}
      compositeAssetId={resourceId}
      itemsActions={itemsActions}
      filterEquities={filterSearchResults}
      emptyContent={emptySearch}
    />
  </div>
}

interface DashboardSearchProps {
  context: IDashboardContext
  close: () => void
  setInstrument: (instrument: SelectedInstrument | null) => void
  selectedInstrument: SelectedInstrument | null
  disableModelSelector?: boolean
  includeBenchmarks?: boolean
  // [TODO:TS] Use proper type once recent instruments typed
  searchType: string
  onInstrumentSelection?: (data: InstrumentSelectionData) => void
}
